import {CommonModule} from '@angular/common';
import {Component, DestroyRef, EventEmitter, Input, Output, inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormsModule} from '@angular/forms';

import {MatButtonModule} from '@angular/material/button';
import {MatButtonToggleModule} from '@angular/material/button-toggle';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatChipsModule} from '@angular/material/chips';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatDividerModule} from '@angular/material/divider';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatSelectModule} from '@angular/material/select';
import {MatSidenavModule} from '@angular/material/sidenav';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {FilterOptions} from '../../../shared/model/filter.model';
import {IFaehigkeit} from '../../../shared/model/faehigkeit.model';
import {VocabularyService} from '../../../services/vocabulary.service';
import {vocabularyName} from '../../../shared/model/vocabulary-name';
import {Katalog} from '../../../shared/model/katalog.model';
import {FilterComponent} from '../../../shared/components/filter/filter.component';
import {Requests} from '../../../shared/model/request.model';
import {DropdownCardFieldComponent} from '../../../shared/components/dropdown-card-field/dropdown-card-field.component';
import {languageDisplayName, LanguageOption, sortLanguageOptions} from '../../../shared/constants/languages';
import {SupportedLanguagesService} from '../../../services/supported-languages.service';
import {
  PROFILER_LANGUAGE_LABEL_KEYS,
  PROFILER_LANGUAGES,
  ProfilerLanguage,
} from '../../../shared/constants/profiler-language';
import {ProfilerLanguageFlagComponent} from '../../../shared/components/profiler-language-flag/profiler-language-flag.component';
import {Advisor} from '../../../shared/model/advisor.model';

@Component({
  selector: 'app-userlist-filter',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    MatSidenavModule,
    MatDividerModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatDatepickerModule,
    MatIconModule,
    MatChipsModule,
    MatSelectModule,
    FilterComponent,
    DropdownCardFieldComponent,
    ProfilerLanguageFlagComponent
  ],
  templateUrl: './userlist-filter.component.html',
  styleUrl: './userlist-filter.component.scss'
})
export class UserlistFilterComponent {
  private _opened = false;

  @Input({required: true})
  get opened(): boolean {
    return this._opened;
  }
  set opened(value: boolean) {
    this._opened = value;
    if (value) this.loadLanguages();
  }
  @Output() openedChange = new EventEmitter<boolean>();

  @Input({required: true}) filter!: FilterOptions;

  @Input() advisorOptions: Advisor[] = [];
  @Input() allIndustries: Requests[] = [];
  @Input() allSkills: IFaehigkeit[] = [];
  @Input() allCatalogues: Katalog[] = [];
  @Input() allCertificates: string[] = [];
  @Input() allMainFocuses: Requests[] = [];
  @Input() allTrainings: Requests[] = [];

  @Output() apply = new EventEmitter<void>();
  @Output() reset = new EventEmitter<void>();

  @Output() deleteProjectCompany = new EventEmitter<number>();
  @Output() editProjectCompany = new EventEmitter<string>();

  @Output() deleteProjectRole = new EventEmitter<number>();
  @Output() editProjectRole = new EventEmitter<string>();

  @Output() deleteEducation = new EventEmitter<number>();
  @Output() editEducation = new EventEmitter<string>();

  @Output() deleteEducationInstitution = new EventEmitter<number>();
  @Output() editEducationInstitution = new EventEmitter<string>();

  // Zuweisung im Konstruktor: im gebuendelten Testlauf laedt der Runner die geteilte
  // Konstanten-Datei verzoegert, ein Feld-Initialisierer liest sie dann als undefined.
  languageOptions: readonly LanguageOption[] = [];
  profilerLanguages: readonly ProfilerLanguage[] = [];

  private readonly destroyRef = inject(DestroyRef);
  private readonly supportedLanguages = inject(SupportedLanguagesService);
  private supportedCodes: string[] = [];
  private languagesLoaded = false;
  private languagesLoading = false;

  constructor(private translate: TranslateService, private vocabulary: VocabularyService) {
    this.profilerLanguages = PROFILER_LANGUAGES;

    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({lang}) => this.sortLanguages(lang));
    this.vocabulary.changes$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.localizeLabels());
  }

  /** Neue Funktionen, damit die OnPush-Auswahllisten nach einem Sprachwechsel neu zeichnen. */
  private localizeLabels(): void {
    this.skillLabel = x => this.vocabulary.skillName(x);
    this.vocabularyLabel = x => vocabularyName(x, this.vocabulary.language);
    this.skillSearchFn = (s, term) => this.matchesSkill(s, term);
  }

  private loadLanguages(): void {
    if (this.languagesLoaded || this.languagesLoading) return;
    this.languagesLoading = true;
    this.supportedLanguages
      .getSupportedLanguages()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: codes => {
          this.languagesLoading = false;
          this.languagesLoaded = true;
          this.supportedCodes = codes;
          this.sortLanguages(this.translate.currentLang ?? 'de');
        },
        error: () => (this.languagesLoading = false),
      });
  }

  private sortLanguages(lang: string): void {
    this.languageOptions = sortLanguageOptions(
      this.supportedCodes,
      code => languageDisplayName(code, key => this.translate.instant(key), lang),
      lang,
    );
  }

  profilerLanguageLabelKey = (language: ProfilerLanguage) =>
    PROFILER_LANGUAGE_LABEL_KEYS[language];

  skillLabel = (x: IFaehigkeit) => this.vocabulary.skillName(x);
  /** Branchen und Schwerpunkte in der Oberflächensprache; Schulungen bleiben deutsch. */
  vocabularyLabel = (x: Requests) => vocabularyName(x, this.vocabulary.language);
  requestLabel = (x: Requests) => x?.name ?? '';
  certificateLabel = (c: string) => c ?? '';
  catalogLabel = (c: Katalog) =>
    c ? `(RVSE${c.rvseVersion}) ${c.bezeichnung ?? ''}` : '';
  languageLabel = (x: LanguageOption) =>
    x?.code ? languageDisplayName(x.code, key => this.translate.instant(key), this.translate.currentLang ?? 'de') : '';
  compareLanguage = (a: LanguageOption, b: LanguageOption) => a?.code === b?.code;

  close(): void {
    this.openedChange.emit(false);
  }

  onApply(): void {
    this.apply.emit();
    this.close();
  }

  onReset(): void {
    this.reset.emit();
  }

  compareRequests = (a: Requests, b: Requests) => a?.id === b?.id;
  compareSkills = (a: IFaehigkeit, b: IFaehigkeit) => a?.id === b?.id;
  compareCatalog = (a: Katalog, b: Katalog) => a?.id === b?.id;
  compareAdvisors = (a: Advisor, b: Advisor) => a?.id === b?.id;

  skillSearchFn = (s: IFaehigkeit, term: string) => this.matchesSkill(s, term);

  private matchesSkill(s: IFaehigkeit, term: string): boolean {
    const t = (term ?? '').toLowerCase().trim();
    if (!t) return true;
    return (
      this.vocabulary.skillName(s).toLowerCase().includes(t) ||
      this.vocabulary.skillCategory(s).toLowerCase().includes(t)
    );
  }

  catalogSearchFn = (c: Katalog, term: string) => {
    const t = (term ?? '').toLowerCase().trim();
    if (!t) return true;

    return this.catalogLabel(c).toLowerCase().includes(t);
  };
}
